import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, ChevronDown } from 'lucide-react';
// Import your logo image
import logoImage from '../../../assets/images/siridharinilogo.jpeg';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [screenWidth, setScreenWidth] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const headerRef = useRef(null);

  // Updated navigation items to match the image
  const navigationItems = [
    { name: 'HOME', href: '/', active: location.pathname === '/' },
    { name: 'Our Story', href: '/ourstory' },
    { name: 'WHAT WE OFFER', href: '/whatweoffer', active: location.pathname === '/whatweoffer' },
    { name: 'OUR FARM', href: '/ourfarm', active: location.pathname === '/ourfarm' },
    { name: 'BLOG', href: '/blogs', active: location.pathname === '/blogs' },
    { name: 'CONTACT', href: '/about/contact', active: location.pathname === '/about/contact' }
  ];

  // Handle screen size detection
  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      setScreenWidth(width);
      setIsMobile(width < 1024);
      
      // Close mobile menu when switching to desktop
      if (width >= 1024) {
        setIsMenuOpen(false);
        setActiveDropdown(null);
      }
    };

    // Initial check
    updateScreenSize();

    // Add event listener
    window.addEventListener('resize', updateScreenSize);
    
    // Cleanup
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  // Handle contact clicks
  const handleEmailClick = useCallback((event) => {
    event.preventDefault();
    window.location.href = 'mailto:info@siridharini.com';
  }, []);

  const handlePhoneClick = useCallback((event) => {
    event.preventDefault();
    window.location.href = 'tel:+919036024568';
  }, []);

  // Check if URL is external
  const isExternalUrl = (url) => {
    return url && (url.startsWith('http://') || url.startsWith('https://'));
  };

  // Navigation handler
  const handleNavigation = useCallback((href, isExternal = false, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!href) return;

    // Close all menus
    setIsMenuOpen(false);
    setActiveDropdown(null);

    // Handle external URLs
    if (isExternal || isExternalUrl(href)) {
      window.location.href = href;
      return;
    }

    // Handle internal navigation
    if (href !== location.pathname) {
      navigate(href);
    }

    // Scroll to top for internal navigation
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  }, [navigate, location.pathname]);

  // Handle mobile menu toggle
  const handleMobileMenuToggle = useCallback((event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setIsMenuOpen(prev => !prev);
    setActiveDropdown(null);
  }, []);

  // Handle outside clicks and cleanup
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setActiveDropdown(null);
        if (isMobile) {
          setIsMenuOpen(false);
        }
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setActiveDropdown(null);
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isMobile]);

  // Close menus when route changes
  useEffect(() => {
    setActiveDropdown(null);
    setIsMenuOpen(false);
  }, [location.pathname]);

  return (
    <>
      {/* Main Navigation - Clean white header like in the image */}
      <nav 
        ref={headerRef}
        style={{
          backgroundColor: 'white',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          borderBottom: '1px solid #e5e7eb'
        }}
      >
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: `0 ${screenWidth < 480 ? '1rem' : screenWidth < 768 ? '1.5rem' : '2rem'}`
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: screenWidth < 480 ? '70px' : screenWidth < 768 ? '80px' : '90px',
            minHeight: '70px'
          }}>
            {/* Logo Section */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center',
              flex: isMobile ? 1 : 'none',
              minWidth: 0,
              maxWidth: isMobile ? 'calc(100% - 60px)' : 'auto'
            }}>
              <button
                onClick={(e) => handleNavigation('/', false, e)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: screenWidth < 480 ? '0.5rem' : '0.75rem',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.5rem',
                  minWidth: 0,
                  overflow: 'hidden'
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: screenWidth < 480 ? '0.5rem' : '0.75rem',
                  minWidth: 0
                }}>
                  {/* Logo Image */}
                  <img 
                    src={logoImage} 
                    alt="Siridharini Logo" 
                    style={{
                      width: screenWidth < 480 ? '45px' : screenWidth < 768 ? '55px' : '65px',
                      height: screenWidth < 480 ? '45px' : screenWidth < 768 ? '55px' : '65px',
                      objectFit: 'contain',
                      flexShrink: 0
                    }}
                  />
                  
                  <div style={{ 
                    textAlign: 'left',
                    minWidth: 0,
                    overflow: 'hidden',
                    flex: 1
                  }}>
                    {/* Company Name - Siridharini style */}
                    <div style={{
                      fontSize: screenWidth < 380 ? '18px' : screenWidth < 480 ? '20px' : screenWidth < 768 ? '24px' : '28px',
                      fontWeight: '700',
                      lineHeight: '1.1',
                      letterSpacing: '0.5px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontFamily: 'Arial, sans-serif'
                    }}>
                      <span style={{ color: '#22c55e' }}>SIRI</span>
                      <span style={{ color: '#f97316' }}>DHARINI</span>
                    </div>
                    <div style={{
                      fontSize: screenWidth < 380 ? '10px' : screenWidth < 480 ? '11px' : screenWidth < 768 ? '12px' : '13px',
                      color: '#6b7280',
                      fontWeight: '500',
                      marginTop: '2px',
                      letterSpacing: '0.5px',
                      display: screenWidth < 380 ? 'none' : 'block',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontFamily: 'Arial, sans-serif'
                    }}>
                      Money Does Grow On Trees
                    </div>
                  </div>
                </div>
              </button>
            </div>

            {/* Desktop Navigation */}
            {!isMobile && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: screenWidth < 1200 ? '1.5rem' : '2.5rem'
              }}>
                {navigationItems.map((item) => (
                  <button
                    key={item.name}
                    onClick={(e) => handleNavigation(item.href, false, e)}
                    style={{
                      padding: '0.75rem 0',
                      fontWeight: '600',
                      fontSize: screenWidth < 1200 ? '13px' : '14px',
                      color: item.active ? '#2d5016' : '#374151',
                      backgroundColor: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      letterSpacing: '0.5px',
                      whiteSpace: 'nowrap',
                      fontFamily: 'Arial, sans-serif',
                      textTransform: 'uppercase',
                      position: 'relative',
                      transition: 'color 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.color = '#2d5016';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.color = item.active ? '#2d5016' : '#374151';
                    }}
                  >
                    {item.name}
                    {/* Active indicator line */}
                    {item.active && (
                      <div style={{
                        position: 'absolute',
                        bottom: '-2px',
                        left: 0,
                        right: 0,
                        height: '2px',
                        backgroundColor: '#2d5016',
                        borderRadius: '1px'
                      }} />
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Mobile Menu Button */}
            {isMobile && (
              <button
                onClick={handleMobileMenuToggle}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: screenWidth < 480 ? '40px' : '45px',
                  height: screenWidth < 480 ? '40px' : '45px',
                  background: 'none',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  color: '#374151',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  flexShrink: 0
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#2d5016';
                  e.target.style.color = '#2d5016';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.color = '#374151';
                }}
              >
                {isMenuOpen ? 
                  <X size={screenWidth < 480 ? 18 : 20} /> : 
                  <Menu size={screenWidth < 480 ? 18 : 20} />
                }
              </button>
            )}
          </div>

          {/* Mobile Navigation */}
          {isMobile && isMenuOpen && (
            <div style={{
              backgroundColor: 'white',
              borderTop: '1px solid #e5e7eb',
              padding: '1.5rem 0',
              display: 'block',
              maxHeight: '70vh',
              overflowY: 'auto',
              overflowX: 'hidden'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {/* Mobile Contact Information */}
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  marginBottom: '1rem',
                  border: '1px solid #e5e7eb'
                }}>
                  <button
                    onClick={handleEmailClick}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#374151',
                      fontSize: '14px',
                      fontWeight: '500',
                      padding: '0.5rem',
                      borderRadius: '4px',
                      width: '100%',
                      marginBottom: '0.5rem',
                      transition: 'all 0.2s ease',
                      fontFamily: 'Arial, sans-serif'
                    }}
                    onTouchStart={(e) => {
                      e.target.style.backgroundColor = '#f3f4f6';
                    }}
                    onTouchEnd={(e) => {
                      setTimeout(() => {
                        e.target.style.backgroundColor = 'transparent';
                      }, 150);
                    }}
                  >
                    <Mail size={16} />
                    <span>info@siridharini.com</span>
                  </button>
                  
                  <button
                    onClick={handlePhoneClick}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#374151',
                      fontSize: '14px',
                      fontWeight: '500',
                      padding: '0.5rem',
                      borderRadius: '4px',
                      width: '100%',
                      transition: 'all 0.2s ease',
                      fontFamily: 'Arial, sans-serif'
                    }}
                    onTouchStart={(e) => {
                      e.target.style.backgroundColor = '#f3f4f6';
                    }}
                    onTouchEnd={(e) => {
                      setTimeout(() => {
                        e.target.style.backgroundColor = 'transparent';
                      }, 150);
                    }}
                  >
                    <Phone size={16} />
                    <span>+91 9036024568</span>
                  </button>
                </div>

                {navigationItems.map((item) => (
                  <button
                    key={item.name}
                    onClick={(e) => handleNavigation(item.href, false, e)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '1rem 1.5rem',
                      borderRadius: '8px',
                      backgroundColor: item.active ? '#f0f9ff' : 'transparent',
                      border: item.active ? '1px solid #2d5016' : '1px solid transparent',
                      fontWeight: '600',
                      fontSize: '15px',
                      color: item.active ? '#2d5016' : '#374151',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      fontFamily: 'Arial, sans-serif',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}
                    onTouchStart={(e) => {
                      if (!item.active) {
                        e.target.style.backgroundColor = '#f9fafb';
                        e.target.style.color = '#2d5016';
                      }
                    }}
                    onTouchEnd={(e) => {
                      if (!item.active) {
                        setTimeout(() => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.color = '#374151';
                        }, 150);
                      }
                    }}
                  >
                    <span style={{ textAlign: 'left', flex: 1 }}>
                      {item.name}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </nav>
    </>
  );
};

export default Header;